import React, { ReactNode, FC, useEffect, useState, useCallback } from 'react'
import PropTypes from 'prop-types' // this seems odd but we still need it for some files
import classnames from 'classnames/bind'
import { Message } from 'semantic-ui-react'
import type { SlottableComponent, AutomationTarget } from '../../../types'
import { ColorNames } from '../../../constants/colorVars'
import Button from '../button'
import Icon from '../icon'
import Text from '../text'
import cls from './styles.scss'
import moment from 'moment'

const cx = classnames.bind(cls)

const defaultIconName: Record<AlertRoleType, string> = {
  info: 'info-circle',
  caution: 'exclamation-triangle',
  error: 'exclamation-circle',
  success: 'check-circle',
  '': '',
}

export const AlertRole = {
  Info: 'info',
  Caution: 'caution',
  Error: 'error',
  Success: 'success',
} as const

export type AlertRoleType = (typeof AlertRole)[keyof typeof AlertRole] | ''

export interface AlertProps
  extends SlottableComponent<'action' | 'secondaryAction'>,
    AutomationTarget {
  role?: AlertRoleType
  type?: 'inline' | 'pageBanner' | 'overlayBanner'
  header?: ReactNode
  content?: ReactNode
  onDismiss?: () => void
  className?: string
  showIcon?: boolean
  icon?: ReactNode
  iconName?: string
  compact?: boolean
  hidden?: boolean
  includeBorder?: boolean
  includeShadow?: boolean
  style?: React.CSSProperties
  contentOnTop?: boolean
  timestamp?: string
  timestampPrefix?: string
  timestampRefreshInMS?: number
}

const Alert: FC<AlertProps> = ({
  role,
  type = 'inline',
  header,
  content,
  slots,
  onDismiss,
  className,
  showIcon,
  icon,
  iconName,
  compact = false,
  includeBorder = false,
  includeShadow = false,
  elmId = 'alert_message',
  contentOnTop = false,
  timestamp,
  timestampPrefix,
  timestampRefreshInMS = 60000,
  ...restProps
}) => {
  const defaultIcon = (role || iconName) && (
    <div className={cx('icon-container', { 'with-timestamp': !!timestamp })}>
      <Icon
        style={role === 'caution' ? { transform: 'translateY(-2px)' } : {}}
        name={iconName || defaultIconName[role ?? '']}
        color='unspecified'
        iconStyle='solid'
        size='big'
        fitted
      />
    </div>
  )
  const iconElement = showIcon && (icon || defaultIcon)
  const wrappedContent =
    typeof content === 'string' ? (
      <Text data-elm-id='content' color='' size='unset'>
        {content}
      </Text>
    ) : (
      content
    )

  const actionSlot = slots?.action?.()
  const augmentedActionSlot =
    React.isValidElement(actionSlot) && actionSlot.type === Button
      ? // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        React.cloneElement(actionSlot, {
          ...actionSlot.props,
          role: 'auxiliary',
          color: role || ColorNames.Gray,
        })
      : actionSlot
  const secondaryActionSlot = slots?.secondaryAction?.()
  const augmentedSecondaryActionSlot =
    React.isValidElement(secondaryActionSlot) &&
    secondaryActionSlot.type === Button
      ? // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        React.cloneElement(secondaryActionSlot, {
          ...secondaryActionSlot.props,
          role: 'auxiliary',
          color: ColorNames.Gray,
        })
      : secondaryActionSlot

  const getElapsedTime = useCallback(() => {
    if (timestamp) {
      const elapsed = moment(timestamp).fromNow()
      const capitalizedElapsed =
        elapsed.charAt(0).toUpperCase() + elapsed.slice(1)
      return timestampPrefix ? elapsed : capitalizedElapsed
    }
    return ''
  }, [timestamp, timestampPrefix])

  const initTimestamp = timestamp ? getElapsedTime() : ''
  const [elapsedTime, setElapsedTime] = useState(initTimestamp)

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>
    function updateElapsedTime() {
      clearTimeout(timeoutId)
      const elapsed = getElapsedTime()
      setElapsedTime(elapsed)
      timeoutId = setTimeout(updateElapsedTime, timestampRefreshInMS)
    }

    if (timestamp) {
      timeoutId = setTimeout(updateElapsedTime, timestampRefreshInMS)
    }

    return () => {
      clearTimeout(timeoutId)
    }
  }, [getElapsedTime, timestamp, timestampPrefix, timestampRefreshInMS])

  const computedStyle: React.CSSProperties = {
    ...(includeBorder ? { border: '1px solid currentColor' } : undefined),
    ...(includeShadow
      ? {
          boxShadow:
            '0 0 8px 1.5px color-mix(in srgb, currentColor 40%, transparent)',
        }
      : undefined),
  }

  return (
    <Message
      className={cx(
        'alert',
        role ? `alert-${role}` : 'alert-none',
        {
          'inline-alert': type === 'inline',
          'page-banner': type === 'pageBanner',
          'overlay-banner': type === 'overlayBanner',
          'include-border': includeBorder,
          'include-shadow': includeShadow,
          compact: compact,
        },
        className
      )}
      style={Object.keys(computedStyle).length ? computedStyle : undefined}
      icon={!!iconElement}
      data-elm-id={elmId}
      onDismiss={onDismiss}
      {...restProps}
    >
      {iconElement}
      <Message.Content>
        {timestamp && (
          <Text size='tiny' color='' className={cx('timestamp')}>
            {timestampPrefix}
            {elapsedTime}
          </Text>
        )}
        <div
          className={cx('header-and-content', {
            'content-on-top': contentOnTop,
          })}
        >
          {header && <Header role={role}>{header}</Header>}
          {wrappedContent}
        </div>
      </Message.Content>
      {(slots?.action || slots?.secondaryAction) && (
        <div
          className={cx('action-content', {
            dismissible: !!onDismiss,
            'with-timestamp': !!timestamp,
          })}
        >
          {augmentedSecondaryActionSlot}
          {augmentedActionSlot}
        </div>
      )}
    </Message>
  )
}

const Header: FC<{
  className?: string
  role?: AlertRoleType
  children: ReactNode
}> = ({ className, children }) => {
  return (
    <Message.Header
      className={cx('alert-header', className)}
      data-elm-id='header'
    >
      {children}
    </Message.Header>
  )
}

interface AlertStatics {
  Header: typeof Header
}

const AlertWithStatics: React.FC<AlertProps> & AlertStatics =
  Alert as unknown as React.FC<AlertProps> & AlertStatics

AlertWithStatics.Header = Header

export default AlertWithStatics

// for support of files that still need this proptype
export const alertSlotsPropType = PropTypes.shape({
  action: PropTypes.func,
  secondaryAction: PropTypes.func,
})
